'use client';

import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { HeroSearch } from '@/hotdesk-ui/components/shared';
import { useAppDispatch } from '@/hotdesk-ui/hooks/hook';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { setGeoLocation, toggelLocationPopup } from '@/hotdesk-ui/store/slices/geo-location.slice';
import { setCurrentSearch } from '@/hotdesk-ui/store/slices/search.slice';
import { StorageKeys } from '@/hotdesk-ui/enum/storage-keys';
import { storeSet } from '@/hotdesk-ui/helpers/utils';
import { appendExploreLocationParams } from '@/hotdesk-ui/helpers/explore-location-params';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import styles from './LocationPages.module.scss';
interface CitySearchProps {
  /** Shown in the box until the visitor types ("New York City, United States"). */
  label: string;
  countryCode?: string;
  /** Explore page of this city — where the search button goes untouched. */
  explorePath: string;
  lang: string;
}

/**
 * The landing page's search bar on a city page: same suggestions, recents and
 * "Nearby", and a picked place opens explore for it the way the landing does.
 * Searching without picking another place opens this city's explore page.
 */
export default function CitySearch({
  label,
  countryCode,
  explorePath,
  lang
}: CitySearchProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const autocompleteInput = useRef<HTMLInputElement>(null);
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const handleSelect = useCallback((result: GeoCodeResult) => {
    result.source = result.source ?? 'autocomplete';
    dispatch(setGeoLocation(result));
    dispatch(setCurrentSearch(result));
    storeSet(StorageKeys.GEO_LOCATION_DATA, result);
    const params = new URLSearchParams({
      lang
    });
    appendExploreLocationParams(params, result);
    firebaseAnalytics('search_clicked', {
      source: 'city_page'
    });
    router.push(`/workspaces?${params}`);
  }, [dispatch, lang, router]);
  const searchCity = useCallback(() => {
    firebaseAnalytics('search_clicked', {
      source: 'city_page'
    });
    router.push(explorePath);
  }, [explorePath, router]);

  // "Nearby" falls back here when the browser has no position to give.
  const askForLocation = useCallback(() => {
    dispatch(toggelLocationPopup(true));
  }, [dispatch]);
  return <HeroSearch className={styles.citySearch} autocompleteInput={autocompleteInput} autocompleteContainer={autocompleteContainer} getPermission={askForLocation} sentData={searchCity} countryCode={countryCode} searchDisplayKey={label} onSelect={handleSelect} data-sentry-element="HeroSearch" data-sentry-component="CitySearch" data-sentry-source-file="CitySearch.tsx" />;
}